import { Button } from '@mui/material';
import { useForm } from 'react-hook-form';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { TextInput } from '@/components/Form/TextInput';

import {
  AMOUNT_PATTERN,
  AMOUNT_WITHOUT_DECIMAL_PATTERN,
  ANNUAL_DEPOSIT_PATTERN,
} from '@/constants/patterns';

import { useAppSelector } from '@/hooks/redux';
import { useQuestionInfoMutation } from '@/hooks/useQuestionInfoMutation';
import { useTranslate } from '@/hooks/useTranslate';

import { selectUserData } from '@/redux/user-data/selectors';

import { getDataTestId } from '@/utils/browser';
import { isCaymanTenant } from '@/utils/tenant';

import { ActionBar } from '../../../../Layout';
import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionForm } from './QuestionForm';

export function QuestionAboutTotalAnnualDeposit({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const { user } = useAppSelector(selectUserData);
  const {
    handleSubmit,
    control,
    formState: { errors: visibleErrors, isValid },
  } = useForm<{
    totalAnnualDeposit: string;
  }>({
    defaultValues: {
      totalAnnualDeposit: '',
    },
    mode: 'onChange',
  });

  const { mutate, isPending, error } = useQuestionInfoMutation({
    onFinished: onAnswered,
    target: props.pepAnswerTarget,
  });

  const onSubmit = handleSubmit(async payload => {
    if (!user) return;

    mutate(payload);
  });

  const amountRegex = isCaymanTenant(user?.tenant)
    ? AMOUNT_WITHOUT_DECIMAL_PATTERN
    : AMOUNT_PATTERN;

  return (
    <QuestionForm
      {...props}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      <TextInput
        type="text"
        control={control}
        name="totalAnnualDeposit"
        error={!!visibleErrors.totalAnnualDeposit}
        helperText={visibleErrors.totalAnnualDeposit?.message}
        rules={{
          required: t('questionDrawer.totalAnnualDeposit.required'),
          validate: value => {
            if (!amountRegex.test(value)) {
              return t('questionDrawer.monthlyDeposit.monthlyDeposit.pattern');
            }
            if (!ANNUAL_DEPOSIT_PATTERN.test(value)) {
              return t('questionDrawer.totalAnnualDeposit.pattern');
            }
            return true;
          },
        }}
        placeholder={t('questionDrawer.totalAnnualDeposit.placeholder')}
        inputMode="numeric"
        autoComplete="off"
        disabled={isPending}
      />
      <ActionBar>
        <Button
          {...getDataTestId(`basic-info-proceed-button`)}
          variant="outlined"
          fullWidth
          type="submit"
          disabled={!isValid || isPending}
        >
          {t('questionDrawer.monthlyDeposit.button.proceed')}
        </Button>
      </ActionBar>
      <FormErrorLogger
        formName="QuestionAboutTotalAnnualDeposit"
        control={control}
      />
    </QuestionForm>
  );
}
